/**
 * Diurnal QC chart rules ported from the Diurnal QC Tool v10.3.26 (MB decisions 251 to 257; Diurnal inventory 02
 * §3.3, §3.6, §4.1, §5.1). Pure: the Field screen (uPlot) and the screenshot and PDF figure (SVG) both read them, so
 * the two always agree.
 *
 * - Colours (decision 256): the tool's, not the brand's: the main dataset black, other basemags blue, grey,
 *   lightblue, orange; TA Live greens and GA/INTERMAGNET olives in turn; survey lines green (start) and red (end).
 *   An external screenshot draws the green and olive lines black (the tool recoloured those pixels).
 * - Metric charts (decision 251): a Y scale per chart, default 12 nT, eight ticks from −Y/6 to Y (Pcal ±Y).
 * - Normalise Y (decision 251): one range for every basemag, padded max(10 nT, 15 %), each offset by 2 %.
 * - Tooltips (decision 253): time, each value to 2 decimals, the gap length, red exceedance reasons.
 * - Clocks (decision 254): UTC, the project time zone (the tool's "Perth") and local time.
 */
import { BASEMAG_FORMAT_LABEL, flightNumberOfTag, type BasemagFormat } from './basemag.js';
import type { DiurnalQc, DiurnalMetric } from './diurnal-qc.js';
import type { Flight } from './model.js';
import { exceedanceReasons } from './diurnal-qc.js';
import { formatOffset, localParts, utcToLocalMs } from './time-model.js';
import type { YScaleKey } from './settings.js';

/**
 * The tool's chart colours as token names; the values live in `brand/tokens/tokens.json` (group `diurnal`, fixed in
 * both themes, never overridden by a tenant) and match uplot-wrapper.js:270-288.
 */
export const DIURNAL_COLOUR_NAMES = [
  'diurnal-primary',
  'diurnal-ds-1',
  'diurnal-ds-2',
  'diurnal-ds-3',
  'diurnal-ds-4',
  'diurnal-ta-1',
  'diurnal-ta-2',
  'diurnal-ta-3',
  'diurnal-ta-4',
  'diurnal-ga-1',
  'diurnal-ga-2',
  'diurnal-ga-3',
  'diurnal-ga-4',
  'diurnal-line-start',
  'diurnal-line-end',
  'diurnal-label',
  'diurnal-exceedance',
  'diurnal-gap',
  'diurnal-gap-edge',
  'diurnal-bad',
  'diurnal-bad-edge',
  'diurnal-threshold',
  'diurnal-grid',
  'diurnal-background',
  'diurnal-mlt-pre-midnight',
  'diurnal-mlt-midnight',
  'diurnal-mlt-dawn',
] as const;

export type DiurnalColour = (typeof DIURNAL_COLOUR_NAMES)[number];

/**
 * The values of the Diurnal QC colours, as `brand/tokens/tokens.json` (group `diurnal`) holds them, for the shared
 * figure (`diurnal-figure.ts`) drawn by Teslyn Field and Teslyn Cloud; packages/domain/test/diurnal-cloud.test.ts
 * keeps the two equal.
 */
export const DIURNAL_COLOUR_VALUES: Readonly<Record<DiurnalColour, string>> = {
  'diurnal-primary': '#000000',
  'diurnal-ds-1': '#0000ff',
  'diurnal-ds-2': '#808080',
  'diurnal-ds-3': '#add8e6',
  'diurnal-ds-4': '#ffa500',
  'diurnal-ta-1': '#1e8c40',
  'diurnal-ta-2': '#3fd03f',
  'diurnal-ta-3': '#9bef9b',
  'diurnal-ta-4': '#0f4f0f',
  'diurnal-ga-1': '#7a8a00',
  'diurnal-ga-2': '#b8d000',
  'diurnal-ga-3': '#ddeb6a',
  'diurnal-ga-4': '#4a5200',
  'diurnal-line-start': '#008000',
  'diurnal-line-end': '#ff0000',
  'diurnal-label': '#000000',
  'diurnal-exceedance': '#ff0000',
  'diurnal-gap': '#ffd700',
  'diurnal-gap-edge': '#b8860b',
  'diurnal-bad': '#ffa500',
  'diurnal-bad-edge': '#aa5500',
  'diurnal-threshold': '#808080',
  'diurnal-grid': '#e6e6e6',
  'diurnal-background': '#ffffff',
  'diurnal-mlt-pre-midnight': 'rgba(255, 150, 50, 0.2)',
  'diurnal-mlt-midnight': 'rgba(220, 60, 60, 0.22)',
  'diurnal-mlt-dawn': 'rgba(255, 200, 50, 0.18)',
};

/** Dataset families with their own colour ramp. */
export type DiurnalFamily = 'basemag' | 'ta_live' | 'observatory';

/** A dataset's format on the Diurnal QC screen: an imported file's, or a TA Live day (sign-off 02-001). */
export type DiurnalFormat = BasemagFormat | 'ta_live';

/** The format's words in the report and screenshot ("Loaded data format", "Data format"). */
export const DIURNAL_FORMAT_LABEL: Readonly<Record<DiurnalFormat, string>> = {
  ...BASEMAG_FORMAT_LABEL,
  ta_live: 'TA Live',
};

/** A basemag format (or `ta_live`) as its colour family: every observatory format is GA/INTERMAGNET. */
export function diurnalFamily(format: string): DiurnalFamily {
  // IAGA-2002, BoM and BGS HAPI observatory data (decision 227): the tool's GA_Data / INTERMAGNET olives
  if (format.startsWith('observatory_')) return 'observatory';
  if (format === 'ta_live') return 'ta_live';
  return 'basemag';
}

const OTHER: readonly DiurnalColour[] = ['diurnal-ds-1', 'diurnal-ds-2', 'diurnal-ds-3', 'diurnal-ds-4'];
const TA: readonly DiurnalColour[] = ['diurnal-ta-1', 'diurnal-ta-2', 'diurnal-ta-3', 'diurnal-ta-4'];
const GA: readonly DiurnalColour[] = ['diurnal-ga-1', 'diurnal-ga-2', 'diurnal-ga-3', 'diurnal-ga-4'];

/**
 * Who will see a screenshot: external draws TA Live lines black (the tool's External Use), leaves the observatory
 * data out (decisions 340, 341: `data-use.ts`) and the MLT shading (decision 337).
 */
export type ScreenshotUse = 'internal' | 'external';

/** Decision 269 (MB, 19 Oct 2026): the daily diurnal PDF goes to clients, so it is drawn in the External colours. */
export const DAILY_PDF_USE: ScreenshotUse = 'external';

/** Where a Diurnal QC figure goes: the screenshot (JPG, clipboard PNG, Export all) or the client daily PDF. */
export type DiurnalFigureDestination = 'screenshot' | 'daily_pdf';

/**
 * The MLT risk windows a figure shades (sign-off 02-040). Decision 313 (MB, 20 Oct 2026): never in the client daily
 * PDF, even while the overlay is on. Decision 337: never in an External screenshot either. An Internal screenshot
 * draws what the screen shows, as the tool's JPG, so it keeps them.
 */
export function figureMltBands<T>(
  bands: readonly T[] | null | undefined,
  destination: DiurnalFigureDestination,
  use: ScreenshotUse,
): readonly T[] {
  if (destination === 'daily_pdf' || use === 'external') return [];
  return bands ?? [];
}

/**
 * Each dataset's colour, as `ui.js:3995-4024`: TA Live and GA datasets take the next shade of their family; the
 * main dataset is black; every other basemag takes blue, grey, lightblue, orange by how many basemags other than
 * the main one come before it.
 */
export function datasetColours(
  families: readonly DiurnalFamily[],
  primary: number,
  use: ScreenshotUse = 'internal',
): DiurnalColour[] {
  let ta = 0;
  let ga = 0;
  let other = 0;
  return families.map((f, i) => {
    if (f === 'ta_live') return use === 'external' ? 'diurnal-primary' : TA[ta++ % TA.length]!;
    if (f === 'observatory') return use === 'external' ? 'diurnal-primary' : GA[ga++ % GA.length]!;
    if (i === primary) return 'diurnal-primary';
    return OTHER[other++ % OTHER.length]!;
  });
}

/**
 * What an External screenshot changes from the screen (decisions 256, 337, 340 and 341), in words for the question;
 * empty when it would change nothing.
 */
export function screenshotUseEffects(families: readonly DiurnalFamily[], mltShown: boolean): string[] {
  const out: string[] = [];
  if (families.includes('ta_live')) out.push('TA Live lines are drawn black, like imported basemag data');
  if (families.includes('observatory')) out.push('observatory and BoM data are left out (staff-only)');
  if (mltShown) out.push('the MLT risk-window shading is left out');
  return out;
}

/**
 * The tool asked Internal or External only when TA Live data was loaded (`ui.js:10420`). Teslyn asks whenever the
 * answer changes the picture: TA Live, observatory or BoM data loaded, or the MLT risk windows shaded.
 */
export function needsScreenshotModePrompt(families: readonly DiurnalFamily[], mltShown: boolean): boolean {
  return screenshotUseEffects(families, mltShown).length > 0;
}

/** Drawing order of the datasets: the main one last, so it lies on top (`ui.js:4700-4720`). */
export function diurnalDrawOrder(n: number, primary: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (i !== primary) out.push(i);
  if (primary >= 0 && primary < n) out.push(primary);
  return out;
}

// ---------------------------------------------------------------------------------------------- Y scales

/** The Y scale every metric chart starts from (index.html: each "Y Scale (nT)" box holds 12). */
export const Y_SCALE_DEFAULT = 12;

/** Where a Y scale comes from (R54, R55): the company default, the project's, or the person's own. */
export type YScaleSource = 'tenant' | 'project' | 'user';

/**
 * Each metric chart's Y scale (R54): the person's own for this project (`userPrefsForProject(...).yScale`), else the
 * project's (Plot 1 and Gradient, Project settings), else the company default (12 nT). `projectValue` is the value
 * before the person's own (what "Reset to project values" restores).
 */
export function resolveYScales(
  project: {
    readonly diurnal_plot1_y_scale_nt?: number | null;
    readonly diurnal_gradient_y_scale_nt?: number | null;
  } | null,
  user: Partial<Readonly<Record<YScaleKey, number>>> | undefined,
): {
  value: Record<YScaleKey, number>;
  source: Record<YScaleKey, YScaleSource>;
  projectValue: Record<YScaleKey, number>;
} {
  const ok = (v: number | null | undefined): v is number =>
    typeof v === 'number' && Number.isFinite(v) && v > 0;
  const fromProject: Partial<Record<YScaleKey, number | null | undefined>> = {
    plot1: project?.diurnal_plot1_y_scale_nt,
    gradient: project?.diurnal_gradient_y_scale_nt,
  };
  const value = {} as Record<YScaleKey, number>;
  const source = {} as Record<YScaleKey, YScaleSource>;
  const projectValue = {} as Record<YScaleKey, number>;
  for (const k of ['plot1', 'plot2', 'gradient', 'pcal', 'dor'] as const) {
    const p = fromProject[k];
    projectValue[k] = ok(p) ? p : Y_SCALE_DEFAULT;
    const u = user?.[k];
    value[k] = ok(u) ? u : projectValue[k];
    source[k] = ok(u) ? 'user' : ok(p) ? 'project' : 'tenant';
  }
  return { value, source, projectValue };
}

export interface MetricAxis {
  readonly min: number;
  readonly max: number;
  readonly ticks: readonly number[];
  readonly decimals: number;
}

/** Decimals of a number as typed (the tool's `getDecimalPrecision` of the nT box). */
function typedDecimals(v: number): number {
  const s = String(v);
  const i = s.indexOf('.');
  return i < 0 ? 0 : s.length - i - 1;
}

/** `getMinimumTickPrecision` (uplot-wrapper.js:658-682): enough decimals to tell the steps apart, at most 2. */
function tickDecimals(step: number, inputPrecision: number): number {
  if (step === 0 || !Number.isFinite(step)) return Math.min(inputPrecision, 2);
  let p = step < 1 ? Math.max(0, Math.ceil(-Math.log10(step))) : 0;
  const str = step.toPrecision(10).replace(/0+$/, '');
  const dot = str.indexOf('.');
  if (dot !== -1) p = Math.max(p, str.length - dot - 1);
  return Math.min(Math.max(p, inputPrecision), 2);
}

/**
 * A metric chart's fixed Y range and ticks from its Y scale (uplot-wrapper.js `createYAxis` / `createYScale` with
 * eight ticks): Pcal ±Y in quarters; every other metric from −Y/6 to Y in sixths, so 0 is the second tick and Y the
 * top one. An empty, zero or negative scale is 12, as the tool's `parseFloat(…) || 12`.
 */
export function metricAxis(metric: DiurnalMetric, yScale: number, thresholdNt: number): MetricAxis {
  const y = Number.isFinite(yScale) && yScale > 0 ? yScale : Y_SCALE_DEFAULT;
  const input = typedDecimals(thresholdNt);
  const round = (v: number): number => Number(v.toFixed(10)) || 0;
  if (metric === 'pcal') {
    const step = y / 4;
    const ticks: number[] = [];
    for (let i = -4; i <= 4; i++) ticks.push(round(step * i));
    return { min: -y, max: y, ticks, decimals: tickDecimals(step, input) };
  }
  const step = y / 6;
  const ticks = [round(-step)];
  for (let i = 0; i <= 6; i++) ticks.push(round(step * i));
  return { min: round(-step), max: y, ticks, decimals: tickDecimals(step, input) };
}

/** Teslyn's per-station auto-fit of the diurnal chart (AC-5.3): pad max(0.5 nT, 10 %), at least 40 nT tall. */
export const DIURNAL_MIN_SPAN_NT = 40;

export function diurnalAutoRange(lo: number, hi: number, minSpan = DIURNAL_MIN_SPAN_NT): [number, number] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1];
  const pad = Math.max(0.5, (hi - lo) * 0.1);
  let a = lo - pad;
  let b = hi + pad;
  if (b - a < minSpan) {
    const m = (a + b) / 2;
    a = m - minSpan / 2;
    b = m + minSpan / 2;
  }
  return [a, b];
}

export interface NormalisedAxis {
  readonly min: number;
  readonly max: number;
  /** Added to the drawn values (2 % of the common range per dataset, so equal lines stay apart). */
  readonly offset: number;
}

/**
 * Normalise Y across datasets (`ui.js:4957-5142`): every basemag (and TA Live) dataset on one range, the global min
 * and max padded by max(10 nT, 15 % of the range), each drawn 2 % of that range higher than the one before it in the
 * list; GA/INTERMAGNET keeps its own axis padded by max(2 nT, 10 %). Null when fewer than two datasets take part or
 * they span 0.1 nT or less (the tool then leaves the axes alone).
 */
export function normalisedRanges(
  datasets: readonly { readonly family: DiurnalFamily; readonly lo: number; readonly hi: number }[],
): { range: number; axes: NormalisedAxis[] } | null {
  const part = datasets.filter((d) => d.family !== 'observatory');
  if (part.length < 2) return null;
  let lo = Infinity;
  let hi = -Infinity;
  for (const d of part) {
    lo = Math.min(lo, d.lo);
    hi = Math.max(hi, d.hi);
  }
  if (!(hi - lo > 0.1)) return null;
  const pad = Math.max(10, (hi - lo) * 0.15);
  const min = lo - pad;
  const max = hi + pad;
  const range = max - min;
  return {
    range,
    axes: datasets.map((d, i) => {
      if (d.family === 'observatory') {
        const p = Math.max(2, (d.hi - d.lo) * 0.1);
        return { min: d.lo - p, max: d.hi + p, offset: 0 };
      }
      return { min, max, offset: i * range * 0.02 };
    }),
  };
}

// ---------------------------------------------------------------------------------------------- times

const two = (n: number): string => String(n).padStart(2, '0');

/** Day of the year of a local-time instant (seconds, epoch-style local clock). */
function jdayOf(localSec: number): number {
  const d = new Date(localSec * 1000);
  return Math.floor((localSec * 1000 - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86_400_000) + 1;
}

/**
 * A chart time as the tool writes it (`formatTimeHHMMSS`, uplot-wrapper.js:468-501, and the axis labels): `HH:MM`
 * or `HH:MM:SS`, with `Day <n> ` in front when the time is past the first day (tooltips) or the data spans several
 * days (axis labels).
 */
export function chartTimeLabel(
  localSec: number,
  opts: { readonly seconds: boolean; readonly firstDay: number; readonly multiDay?: boolean },
): string {
  const s = Math.floor(localSec);
  const sod = ((s % 86_400) + 86_400) % 86_400;
  const text = `${two(Math.floor(sod / 3600))}:${two(Math.floor((sod % 3600) / 60))}${opts.seconds ? `:${two(sod % 60)}` : ''}`;
  const pastFirst = Math.floor(s / 86_400) !== Math.floor(opts.firstDay / 86_400);
  return opts.multiDay || pastFirst ? `Day ${jdayOf(s)} ${text}` : text;
}

// ---------------------------------------------------------------------------------------------- tooltips

export type TooltipTone = 'plain' | 'gap' | 'fail';

/** One tooltip line: an optional series label, then text in the tone's colour (gold gap, red exceedance). */
export interface TooltipLine {
  readonly label?: string;
  readonly text: string;
  readonly tone: TooltipTone;
}

/** A gap length as the tool writes it: `45s`, `5m 30s`, `2h 5m` (uplot-wrapper.js:1328-1340). */
export function formatGapDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** What the diurnal tooltip says about one reading: the gap it borders (s) and its exceedance reasons. */
export function tooltipDetail(qc: DiurnalQc, i: number): { gapSec: number | null; reasons: string[] } {
  let gapSec: number | null = null;
  if (qc.gapAfter[i] && i + 1 < qc.n) gapSec = (qc.tMs[i + 1]! - qc.tMs[i]!) / 1000;
  else if (i > 0 && qc.gapAfter[i - 1]) gapSec = (qc.tMs[i]! - qc.tMs[i - 1]!) / 1000;
  return { gapSec, reasons: qc.exceed[i] ? exceedanceReasons(qc, i) : [] };
}

export interface TooltipItem {
  readonly label: string;
  /** Null when the dataset has no reading here. */
  readonly value: number | null;
  readonly gapSec: number | null;
  readonly reasons: readonly string[];
}

/**
 * The diurnal chart's tooltip (uplot-wrapper.js:1343-1420): `Time: …`, each dataset's value to 2 decimals, then at
 * the bottom the first gap found (gold) and every exceedance reason (red).
 */
export function diurnalTooltip(time: string, items: readonly TooltipItem[]): TooltipLine[] {
  const out: TooltipLine[] = [{ text: `Time: ${time}`, tone: 'plain' }];
  const extra: TooltipLine[] = [];
  let gap = false;
  for (const it of items) {
    if (it.value === null || !Number.isFinite(it.value)) continue;
    out.push({ label: it.label, text: it.value.toFixed(2), tone: 'plain' });
    if (!gap && it.gapSec !== null) {
      extra.push({ text: `⚠ Gap: ${formatGapDuration(it.gapSec)}`, tone: 'gap' });
      gap = true;
    }
    for (const r of it.reasons) extra.push({ text: `⚠ ${r}`, tone: 'fail' });
  }
  return [...out, ...extra];
}

/**
 * A metric chart's tooltip (uplot-wrapper.js:1296-1326, 1421-1432): the value to 2 decimals, red with
 * `⚠ Exceedance: x.xnT` when it is over the threshold (Pcal on its absolute value).
 */
export function metricTooltip(
  time: string,
  label: string,
  value: number,
  thresholdNt: number,
  symmetric: boolean,
): TooltipLine[] {
  const v = symmetric ? Math.abs(value) : value;
  const over = v > thresholdNt;
  const out: TooltipLine[] = [
    { text: `Time: ${time}`, tone: 'plain' },
    { label, text: value.toFixed(2), tone: over ? 'fail' : 'plain' },
  ];
  if (over) out.push({ text: `⚠ Exceedance: ${(v - thresholdNt).toFixed(1)}nT`, tone: 'fail' });
  return out;
}

/** Series labels the tool's tooltips use (ui.js:4058, 5253, 6892, 7085, 7583, 7753). */
export const METRIC_TOOLTIP_LABEL: Readonly<Record<DiurnalMetric, string>> = {
  plot1: 'Plot 1 nT',
  plot2: 'Plot 2 nT',
  gradient: 'Gradient (nT)',
  pcal: 'Pcal nT',
  dor: 'Std Dev of Residuals (nT)',
};

export const diurnalSeriesLabel = (name: string): string => `Diurnal - ${name}`;

// ---------------------------------------------------------------------------------------------- clocks

/**
 * The toolbar clocks (index.html:2530-2558): UTC, the project time zone (the tool's fixed "Perth") and the
 * computer's own time, `HH:MM:SS`. `localOffsetMin` is `Date.getTimezoneOffset()` (minutes behind UTC).
 */
export function clockTexts(
  nowMs: number,
  projectOffsetH: number,
  localOffsetMin: number,
): { utc: string; project: string; local: string } {
  const hms = (ms: number): string => {
    const d = new Date(ms);
    return `${two(d.getUTCHours())}:${two(d.getUTCMinutes())}:${two(d.getUTCSeconds())}`;
  };
  return {
    utc: `UTC ${hms(nowMs)}`,
    project: `Project UTC${formatOffset(projectOffsetH)} ${hms(nowMs + Math.round(projectOffsetH * 3_600_000))}`,
    local: `Local ${hms(nowMs - localOffsetMin * 60_000)}`,
  };
}

// ---------------------------------------------------------------------------------------------- navigation

/**
 * Chart navigation (decision 260), the Diurnal tool's (uplot-wrapper.js:3129-3342): the mouse wheel zooms ×0.9 in
 * and ×1.1 out about the pointer, a left drag pans, a right drag draws a zoom box, a double click resets every chart.
 * Without its bugs: the pointer is read from the plot's own CSS box, never mixed with canvas pixels, so the zoom stays
 * about the pointer at any display scaling (02-28); the zoom is kept when the data refresh (02-29, the screen's shared
 * range); the bounds are the readings and the survey lines, so a line flown past the basemag can be reached (02-30).
 */
export type TimeRange = readonly [number, number];

/** The smallest time span a zoom may show, s (the tool's 10 s). */
export const MIN_ZOOM_SPAN_S = 10;

/** Where the pointer is across the plot, 0 to 1, from CSS pixels only. */
export function pointerRatio(
  clientX: number,
  plot: { readonly left: number; readonly width: number },
): number {
  if (!(plot.width > 0)) return 0.5;
  return Math.max(0, Math.min(1, (clientX - plot.left) / plot.width));
}

/** The view after one wheel step (deltaY > 0 zooms out), null when it would show less than 10 s. */
export function wheelZoom(
  view: TimeRange,
  ratio: number,
  deltaY: number,
  bounds: TimeRange,
): [number, number] | null {
  const range = view[1] - view[0];
  const next = range * (deltaY > 0 ? 1.1 : 0.9);
  const at = view[0] + ratio * range;
  const min = Math.max(bounds[0], at - ratio * next);
  const max = Math.min(bounds[1], at + (1 - ratio) * next);
  return max - min >= MIN_ZOOM_SPAN_S ? [min, max] : null;
}

/** The view after a drag of `dxPx` CSS pixels across a plot `widthPx` wide, kept inside the bounds. */
export function panView(
  start: TimeRange,
  dxPx: number,
  widthPx: number,
  bounds: TimeRange,
): [number, number] {
  const range = start[1] - start[0];
  const d = widthPx > 0 ? -(dxPx / widthPx) * range : 0;
  let min = start[0] + d;
  let max = start[1] + d;
  if (min < bounds[0]) {
    min = bounds[0];
    max = bounds[0] + range;
  }
  if (max > bounds[1]) {
    max = bounds[1];
    min = bounds[1] - range;
  }
  return [min, max];
}

/** The view a zoom box from `aPx` to `bPx` selects (CSS pixels from the plot's left), null under 20 px or 10 s. */
export function boxZoom(view: TimeRange, aPx: number, bPx: number, widthPx: number): [number, number] | null {
  if (Math.abs(bPx - aPx) < 20 || !(widthPx > 0)) return null;
  const r = (px: number): number => Math.max(0, Math.min(1, px / widthPx));
  const range = view[1] - view[0];
  const min = view[0] + r(Math.min(aPx, bPx)) * range;
  const max = view[0] + r(Math.max(aPx, bPx)) * range;
  return max - min >= MIN_ZOOM_SPAN_S ? [min, max] : null;
}

// ---------------------------------------------------------------------------------------------- the chosen file's days

const DAY_MS = 86_400_000;

/**
 * The local days the shown datasets cover (MB's R27): every day from each file's first reading to its last, as the
 * local start of the day (ms, local clock) and its day of year, in order. A day file is one day; a night file two.
 */
export function readingDays(
  spans: readonly (readonly [number, number])[],
  offsetH: number,
): [number, number][] {
  const days = new Map<number, number>();
  for (const [first, last] of spans) {
    const a = Math.floor(utcToLocalMs(first, offsetH) / DAY_MS) * DAY_MS;
    const b = Math.floor(utcToLocalMs(last, offsetH) / DAY_MS) * DAY_MS;
    for (let d = a; d <= b; d += DAY_MS) {
      // the day of year of the local day: its noon, back on the UTC clock
      days.set(d, localParts(d + DAY_MS / 2 - offsetH * 3_600_000, offsetH).jday);
    }
  }
  return [...days.entries()].sort((x, y) => x[0] - y[0]);
}

/** The survey lines that touch one of the local days (`readingDays`), so the charts show the file's day or days. */
export function linesOnDays<L extends { readonly start_at: string; readonly end_at: string }>(
  lines: readonly L[],
  days: readonly number[],
  offsetH: number,
): L[] {
  return lines.filter((l) => {
    const a = utcToLocalMs(Date.parse(l.start_at), offsetH);
    const b = utcToLocalMs(Date.parse(l.end_at), offsetH);
    return days.some((d) => a < d + DAY_MS && b >= d);
  });
}

/** A flight of a shown day whose survey lines are not imported, and the folder its `_Diurnal.XYZ` file would be in. */
export interface FlightWithoutLines {
  readonly flight: Flight;
  /** The flight's folder (`…\F26044_Northern_Area\2. Flights\Day_307\Flt004`), or null when Teslyn has none. */
  readonly folder: string | null;
}

/**
 * The flights of the shown days with no survey lines on the charts (MB, 3 Nov 2026: F26044 day 307 was flown in two
 * blocks, the morning flight in the Southern block and the afternoon flight in the Northern, and the basemag's
 * import had read only its own block's `_Diurnal.XYZ`, so the afternoon's lines were missing with nothing said).
 * A flight counts when its date is one of the days and no shown line's flight tag carries its number
 * (`flightNumberOfTag`); `days` are the local starts of the shown days (`readingDays`), `shown` the lines on them
 * (`linesOnDays`). In flight number order.
 */
export function flightsWithoutLines<L extends { readonly flight_tag: string }>(
  flights: readonly Flight[],
  shown: readonly L[],
  days: readonly number[],
): FlightWithoutLines[] {
  const dates = new Set(days.map((d) => new Date(d).toISOString().slice(0, 10)));
  const withLines = new Set(shown.map((l) => flightNumberOfTag(l.flight_tag)));
  return flights
    .filter((f) => f.deleted_at === null && dates.has(f.date) && !withLines.has(f.flt_num))
    .sort((a, b) => a.flt_num - b.flt_num)
    .map((flight) => ({
      flight,
      folder: flight.folders[0] ?? (flight.log_path ? parentFolder(flight.log_path) : null),
    }));
}

function parentFolder(path: string): string | null {
  const at = Math.max(path.lastIndexOf('\\'), path.lastIndexOf('/'));
  return at > 0 ? path.slice(0, at) : null;
}

/** The margin either side of the charts' time axis (R27): 2 % of what is drawn, at least 5 minutes, in seconds. */
export function chartMarginS(spanS: number): number {
  return Math.max(300, Math.round(spanS * 0.02));
}
